import { Client, MergeClientFieldValues } from "../../../types/client";
import { formatPhoneNumber } from "../../../utils/phoneUtils";

/**
 * A user-reconcilable field on the "Choose Fields" merge step. Some logical
 * fields (phone, address) map to several underlying scalar columns that must
 * move together, so each descriptor lists the concrete `fields` it copies.
 */
export interface MergeField {
  key: string;
  label: string;
  /** Underlying client columns copied when this field's record is chosen. */
  fields: (keyof MergeClientFieldValues)[];
  /** Human-readable value for display / same-value comparison. */
  format: (client: Client) => string;
}

const formatYesNo = (value?: boolean) => (value ? "Yes" : "No");

const formatAddress = (c: Client): string => {
  const parts = [c.address_1, c.address_2, c.city, c.state, c.zip_code].filter(
    (p) => p != null && `${p}`.trim() !== "",
  );
  return parts.length ? parts.join(", ") : "-";
};

const formatPhone = (c: Client): string => {
  if (!c.area_code && !c.phone_number) return "-";
  return formatPhoneNumber(`${c.area_code ?? ""}${c.phone_number ?? ""}`);
};

/** Ordered list of fields shown side-by-side on the Choose Fields step. */
export const MERGE_FIELDS: MergeField[] = [
  {
    key: "first_name",
    label: "First Name",
    fields: ["first_name"],
    format: (c) => c.first_name || "-",
  },
  {
    key: "last_name",
    label: "Last Name",
    fields: ["last_name"],
    format: (c) => c.last_name || "-",
  },
  {
    key: "phone",
    label: "Phone Number",
    fields: ["country_code", "area_code", "phone_number", "phone_number_ext"],
    format: formatPhone,
  },
  {
    key: "email",
    label: "Email",
    fields: ["email"],
    format: (c) => c.email || "-",
  },
  {
    key: "address",
    label: "Address",
    fields: ["address_1", "address_2", "city", "state", "zip_code"],
    format: formatAddress,
  },
  {
    key: "gender",
    label: "Gender",
    fields: ["gender"],
    format: (c) => c.gender || "-",
  },
  {
    key: "pronouns",
    label: "Pronouns",
    fields: ["pronouns"],
    format: (c) => c.pronouns || "-",
  },
  {
    key: "birthday",
    label: "Birthday",
    fields: ["birthday"],
    format: (c) => c.birthday || "-",
  },
  {
    key: "is_opted_in_reminder",
    label: "Appointment Reminders",
    fields: ["is_opted_in_reminder"],
    format: (c) => formatYesNo(c.is_opted_in_reminder),
  },
  {
    key: "is_opted_in_marketing",
    label: "Marketing Opt-In",
    fields: ["is_opted_in_marketing"],
    format: (c) => formatYesNo(c.is_opted_in_marketing),
  },
];

/**
 * True when every record formats to the same value for this field — in which
 * case the UI shows a "SAME VALUE" chip instead of a chooser.
 */
export function isSameValue(field: MergeField, records: Client[]): boolean {
  if (records.length < 2) return true;
  const first = field.format(records[0]);
  return records.every((r) => field.format(r) === first);
}

/**
 * Build the `field_values` payload from the user's per-field winning-record
 * selections. Fields the user didn't explicitly choose (e.g. same-value ones)
 * fall back to the winning record so the surviving profile is fully specified.
 *
 * NOTE: blank scalar choices cannot CLEAR a field. `ClientContext.mergeClients`
 * runs `removeNullValues(fieldValues)`, which strips `""` (and null/undefined)
 * before sending — so if a user picks a record whose value is empty to wipe,
 * say, a stale email, that empty string is dropped and the backend keeps the
 * winner's existing non-blank value. Booleans are unaffected (`false` survives
 * the filter). Intentional clears are not a supported operation today; the
 * field picker is for choosing between non-blank values across records.
 *
 * @param records all records in the merge
 * @param winningClientId uuid of the surviving record (default source)
 * @param selections map of field key -> chosen record uuid
 */
export function buildFieldValues(
  records: Client[],
  winningClientId: string,
  selections: Record<string, string>,
): MergeClientFieldValues {
  const byId = new Map(records.map((r) => [r.id, r]));
  const result: Record<string, unknown> = {};

  for (const field of MERGE_FIELDS) {
    const sourceId = selections[field.key] || winningClientId;
    const source = byId.get(sourceId) || byId.get(winningClientId);
    if (!source) continue;
    const sourceRecord = source as unknown as Record<string, unknown>;
    for (const col of field.fields) {
      result[col] = sourceRecord[col];
    }
  }

  return result as MergeClientFieldValues;
}
